import {
	ASSET_TYPE_GROUP_LABEL,
	ASSET_TYPE_GROUP_ORDER,
	assetTypeGroup,
	DEFAULT_ASSET_TYPE_TITLE,
	isDefaultAssetTypeTitle,
	rotationForDefaultAssetType,
} from '@ikegps/core-api';
import type { FormModelFieldAssetType, FormModelFieldAssetTypeOption } from '@ikegps/core-shared';
import { observable } from 'mobx';
import { AssetTypeSwatch } from '../../components/asset.type.swatch';
import { ModalService } from '../../components/modal/modal.service';
import { SearchInput } from '../../components/search.input/search.input';
import { addStyles, DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';

const CSS = {
	container: 'c-AssetTypeModal',
	search: 'c-AssetTypeModal__search',
	list: 'c-AssetTypeModal__list',
	group: 'c-AssetTypeModal__group',
	row: 'c-AssetTypeModal__row',
	selected: 'c-AssetTypeModal__row--selected',
	name: 'c-AssetTypeModal__name',
	empty: 'c-AssetTypeModal__empty',
};

addStyles({
	[CSS.container]: { width: '360px', 'max-width': '100%', display: 'flex', 'flex-direction': 'column' },
	[CSS.search]: { 'margin-bottom': DefaultTheme.space(1) },
	// A fixed height (not just a cap) keeps the modal from resizing as the search narrows the match count.
	[CSS.list]: { height: '320px', 'overflow-y': 'auto' },
	[CSS.group]: {
		'font-weight': 'bold',
		color: DefaultTheme.font.color.supporting,
		padding: `${DefaultTheme.space(1)} ${DefaultTheme.space(1)} 4px`,
	},
	[CSS.row]: {
		display: 'flex',
		'align-items': 'center',
		gap: '10px',
		padding: '6px 10px',
		cursor: 'pointer',
		'border-left': '3px solid transparent',
	},
	[CSS.selected]: { 'border-left': `3px solid ${DefaultTheme.color.accent}`, background: '#f6f7f8' },
	[CSS.name]: { flex: '1 1 auto' },
	[CSS.empty]: { padding: DefaultTheme.space(2), color: DefaultTheme.font.color.supporting },
});

interface PickerState {
	selected: string;
	search: string;
}

/**
 * The asset types a user may pick from. In a non-Pro department (`onlyCollection`) the picker is
 * restricted to the default "Collection" type — the only asset type a standard department is allowed to
 * set — so a required asset type can still be re-selected after clearing it (UFO-1733).
 */
export function selectableAssetTypes(
	schema: FormModelFieldAssetType,
	onlyCollection: boolean
): FormModelFieldAssetTypeOption[] {
	if (!onlyCollection) {
		return schema.visibleAssetTypes;
	}
	return schema.visibleAssetTypes.filter((option) => option.title === DEFAULT_ASSET_TYPE_TITLE);
}

/** Open the "Select Asset Type" picker. Resolves to the chosen type's title, or null on cancel. */
export async function openAssetTypeModal(
	schema: FormModelFieldAssetType,
	current: string,
	onlyCollection = false
): Promise<string | null> {
	// Observable so row clicks / search input trigger a reactive re-render of the modal content.
	const state: PickerState = observable({ selected: current, search: '' });
	const result = await ModalService.open({
		title: T.ASSET_TYPE_MODAL_TITLE,
		hasCancel: true,
		okName: T.OK,
		// Required contract: an asset type must be chosen to confirm.
		okDisabled: () => state.selected === '',
		content: () => viewPicker(schema, state, onlyCollection),
	});
	return result.ok ? state.selected : null;
}

function viewPicker(schema: FormModelFieldAssetType, state: PickerState, onlyCollection: boolean): MithrilVNode {
	const search = state.search.trim().toLowerCase();
	const options = selectableAssetTypes(schema, onlyCollection).filter(
		(option) => search === '' || option.title.toLowerCase().includes(search)
	);

	// Presets group by category; anything else (user-added) is custom and grouped separately at the end.
	const presets = options.filter((option) => isDefaultAssetTypeTitle(option.title));
	const custom = options.filter((option) => !isDefaultAssetTypeTitle(option.title));

	const groups: MithrilVNode[] = [];
	for (const group of ASSET_TYPE_GROUP_ORDER) {
		const inGroup = presets.filter((option) => assetTypeGroup(option.title) === group);
		if (inGroup.length === 0) {
			continue;
		}
		groups.push(m('div', { className: CSS.group }, Translate(ASSET_TYPE_GROUP_LABEL[group])));
		for (const option of inGroup) {
			groups.push(viewRow(option, state));
		}
	}
	if (custom.length > 0) {
		groups.push(m('div', { className: CSS.group }, Translate(T.ASSET_TYPE_CUSTOM_GROUP)));
		for (const option of custom) {
			groups.push(viewRow(option, state));
		}
	}

	return m('div', { className: CSS.container }, [
		SearchInput.view({
			className: CSS.search,
			placeholder: Translate(T.ASSET_TYPE_SEARCH),
			value: state.search,
			onValue: (next) => {
				state.search = next;
			},
		}),
		// Always the same fixed-height wrapper (rows or the empty message) so the modal never resizes as
		// the search narrows the match count.
		m(
			'div',
			{ className: CSS.list },
			groups.length > 0 ? groups : m('div', { className: CSS.empty }, Translate(T.ASSET_TYPE_NO_RESULTS))
		),
	]);
}

function viewRow(option: FormModelFieldAssetTypeOption, state: PickerState): MithrilVNode {
	const isSelected = state.selected === option.title;
	return m(
		'div',
		{
			className: isSelected ? `${CSS.row} ${CSS.selected}` : CSS.row,
			onclick: () => {
				state.selected = option.title;
			},
		},
		[
			AssetTypeSwatch.view({
				icon: option.icon,
				color: option.color,
				rotate: rotationForDefaultAssetType(option.title),
			}),
			m('div', { className: CSS.name }, option.title),
		]
	);
}
